import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import styles from './OpportunityFilter.module.scss';
import OpportunityFilterLogic from './OpportunityFilter.logic';
import { Filter, Plus } from 'lucide-react';
import './test.scss';
import AddOpportunity from '../AddOpportunity/AddOpportunity';
import { MultiSelect } from 'primereact/multiselect';
import { Avatar } from 'primereact/avatar';
import { Agency, Source, User } from 'Interfaces/BDPortalInterfaces';
import { getInitials } from 'utils/getInitials';
import { getBackgroundColor } from 'utils/avatarUtilities';
import PortfolioOpportunitiesModal from '../PortfolioOpportunities/PortfolioOpportunitiesModal';
import AsyncSelect from 'react-select/async';
import { Calendar } from 'primereact/calendar';
import { Slider, SliderChangeEvent } from 'primereact/slider';
import FilterDeleteIcon from 'assets/icons/filterDeleteIcon.svg';
import ClearIcon from 'assets/icons/clearIcon.svg';
import { useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';

interface OpportunityFilterProps {
  boardType: string;
  setBoardType: (boardType: string) => void;
  viewType: 'Board' | 'List' | 'PortfolioOpportunities';
  handleBoardClick?: () => void;
  handleListClick?: () => void;
  isPortfolioFilterVisible?: boolean;
}

const OpportunityFilter = ({
  boardType,
  setBoardType,
  viewType,
  handleBoardClick: _handleBoardClick = () => {},
  handleListClick: _handleListClick = () => {},
  isPortfolioFilterVisible = true,
}: OpportunityFilterProps) => {
  const {
    handleClickFilter,
    isFilterVisible,
    handleViewChange,
    options,
    handleOpenNewOpportunityModal,
    handleCloseNewOpportunityModal,
    isNewOpportunityModalvisible,
    dropdownDetails,
    searchKeyword,
    filterAnalyst,
    filterAccManagers,
    filterStage,
    filterStatus,
    setSearchKeyword,
    setFilterStage,
    setFilterStatus,
    handleAnalytisSelect,
    handleAccManagersSelect,
    handleClearFilters,
    handleIterationTypeChange,
    isPortfolioOpportunitiesModalVisible,
    handleOpenPortfolioOpportunitiesModal,
    handleClosePortfolioOpportunitiesModal,
    handleSourceSelect,
    handleAgencySelect,
    handlePrimarySupplierSelect,
    filterSource,
    filterAgency,
    filterPrimarySupplier,
    filterIsPortfolioOpportunity,
    fetchSuggestionsWithDelayForSuppliers,
    handleMoleculeSearchInputFocus,
    handleMoleculeSearchInputFocusOut,
    isMoleculeSearchInputFocussed,
    handleFilterStartAneEndDatesChange,
    filterStartAndEnddates,
    handleFilterEstimatedAnnualRevenue,
    filterAnnualRevenueRange,
    handleIsPortfoliOpportunityOptionSelect,
    handleToggleAdvancedFilters,
    isAdvancedFiltersVisible,
  } = OpportunityFilterLogic(boardType, viewType, setBoardType);
  const op = useRef<OverlayPanel | null>(null);

  const getAssigneedOptions = () => {
    if (dropdownDetails && dropdownDetails[boardType as 'NC' | 'MAS'].User) {
      const tempOptions: User[] = [];

      tempOptions.push({
        ID: 0,
        Name: 'Unassigned',
      });
      dropdownDetails[boardType as 'NC' | 'MAS'].User?.forEach(option => {
        tempOptions.push(option);
      });
      return tempOptions;
    }
    return [];
  };

  const getSourceOptions = () => {
    if (dropdownDetails && dropdownDetails[boardType as 'NC' | 'MAS'].Source) {
      const tempOptions: Source[] = [];
      dropdownDetails[boardType as 'NC' | 'MAS'].Source?.forEach(option => {
        tempOptions.push(option);
      });
      return tempOptions;
    }
    return [];
  };

  const getAgencyOptions = () => {
    if (dropdownDetails && dropdownDetails[boardType as 'NC' | 'MAS'].Agency) {
      const tempOptions: Agency[] = [];

      tempOptions.push({
        SourceID: 0,
        SourceName: 'No Agency',
      });
      dropdownDetails[boardType as 'NC' | 'MAS'].Agency?.forEach(option => {
        tempOptions.push(option);
      });
      return tempOptions;
    }
    return [];
  };

  const getOpportunityTypeOptions = () => {
    return [
      {
        ID: 'Yes',
        label: 'Portfolio',
      },
      {
        ID: 'No',
        label: 'Non Portfolio',
      },
    ];
  };

  const assigneeOptionTemplate = (option: User | null) => {
    return (
      <>
        {option ? (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {option.ID > 0 && (
              <Avatar
                title={option.Name}
                id="profileAvatar"
                shape="circle"
                label={option.Name && getInitials(option?.Name)}
                style={{
                  backgroundColor: getBackgroundColor(option.Name[0]),
                  color: '#522D6E',
                  marginRight: '0.5rem',
                }}
              />
            )}
            <div>{option.Name}</div>
          </div>
        ) : (
          <span>Unassigned</span>
        )}
      </>
    );
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    if (op.current) {
      op.current.toggle(e);
    }
  };

  return (
    <div
      className="bdportal flex flex-col OpportunityFilterBox"
      style={{
        padding: '20px',
        paddingBottom: '0',
        margin: '0 auto',
      }}
    >
      <div className="bdportal flex justify-between items-center">
        <div className={styles.dropdown}>
          <Dropdown
            options={[
              { label: 'NC', value: 'NC' },
              { label: 'MAS', value: 'MAS' },
            ]}
            value={boardType}
            onChange={e => handleIterationTypeChange(e.value)}
            placeholder="Select Board"
            className={styles.selectDropdown}
          />
        </div>
        <div className="bdportal flex items-center">
          <SelectButton
            value={viewType}
            onChange={e => handleViewChange(e.value)}
            options={options}
            optionLabel="icon"
            optionValue="name"
            itemTemplate={option => (
              <div>
                <img src={option.icon}></img>
              </div>
            )}
            className="bdportal mr-2"
            unselectable={false}
          />
          <button
            onClick={handleClickFilter}
            className="bdportal p-4 rounded-lg mr-2"
            style={{background: "var(--bg-navbar)", color: "white"}}
          >
            <Filter size={16} />
          </button>
          <button
            onClick={handleOpenNewOpportunityModal}
            className="bdportal p-4 rounded-lg flex items-center"
            style={{background: "var(--bg-navbar)", color: "white"}}
            disabled={!viewType}
          >
            <Plus size={16} className="bdportal mr-2" />
            New Opportunity
          </button>
          <button
            onClick={handleOpenPortfolioOpportunitiesModal}
            className="bdportal ml-2 p-4 rounded-lg flex items-center"
            style={{background: "var(--bg-navbar)", color: "white"}}
            disabled={!viewType}
          >
            Create Portfolio Opportunities
          </button>
        </div>
      </div>
      {/* Filters */}
      {(boardType === 'NC' || boardType === 'MAS') && (
        <div className="bdportal relative">
          {isFilterVisible && (
            <>
              {/* Group 1 - Basic Filters (Always Visible) */}
              <div
                className={`bdportal flex items-center ${styles.filterGroup} ${styles.basicFilters}`}
                style={{ minHeight: '3.5rem' }}
              >
                <div className="bdportal flex-1">
                  <InputText
                    onFocus={handleMoleculeSearchInputFocus}
                    onBlur={handleMoleculeSearchInputFocusOut}
                    type="text"
                    placeholder="Search by keyword"
                    value={searchKeyword}
                    onChange={e => setSearchKeyword(e.target.value)}
                    className="bdportal w-full p-4 border rounded"
                    style={{ maxHeight: '100%', background: "var(--bg-table-row)", border: '1px solid var(--border-primary)', color: "var(--text-primary)" }}
                  />
                </div>

                {!isMoleculeSearchInputFocussed && (
                  <>
                    <MultiSelect
                      selectAllLabel="Select All"
                      value={filterSource}
                      onChange={handleSourceSelect}
                      options={getSourceOptions()}
                      optionLabel="Source"
                      optionValue="ID"
                      className="bdportal p-1 border"
                      placeholder="Select Sources"
                      maxSelectedLabels={1}
                      selectedItemsLabel={`${filterSource?.length} Sources selected`}
                      style={{
                        maxWidth: '15rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      showClear
                    />

                    <MultiSelect
                      selectAllLabel="Select All"
                      value={filterAgency}
                      onChange={handleAgencySelect}
                      options={getAgencyOptions()}
                      optionLabel="SourceName"
                      optionValue="SourceID"
                      className="bdportal p-1 border"
                      placeholder="Select Agencies"
                      maxSelectedLabels={1}
                      selectedItemsLabel={`${filterAgency?.length} Agencies selected`}
                      style={{
                        maxWidth: '15rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      showClear
                    />

                    <MultiSelect
                      itemTemplate={assigneeOptionTemplate}
                      selectAllLabel="Select All"
                      value={filterAnalyst}
                      onChange={handleAnalytisSelect}
                      options={getAssigneedOptions()}
                      optionLabel="Name"
                      optionValue="ID"
                      className="bdportal p-1 border"
                      placeholder="Select Analysts"
                      maxSelectedLabels={1}
                      selectedItemsLabel={`${filterAnalyst?.length} Analysts selected`}
                      style={{
                        maxWidth: '15rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                      }}
                      showClear
                    />

                    <MultiSelect
                      itemTemplate={assigneeOptionTemplate}
                      selectAllLabel="Select All"
                      value={filterAccManagers}
                      onChange={handleAccManagersSelect}
                      options={getAssigneedOptions()}
                      optionLabel="Name"
                      optionValue="ID"
                      className="bdportal p-1 border"
                      placeholder="Select Acc Managers"
                      maxSelectedLabels={1}
                      selectedItemsLabel={`${filterAccManagers?.length} Managers selected`}
                      style={{
                        maxWidth: '15rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                      }}
                      showClear
                    />

                    <Button
                      onClick={handleClearFilters}
                      style={{
                        background: 'var(--bg-navbar)',
                        color: 'white',
                        padding: '1rem',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        style={{ height: '23px', width: 'auto' }}
                        src={FilterDeleteIcon}
                        alt="Clear Filters"
                      />
                    </Button>

                    <Button
                      onClick={handleToggleAdvancedFilters}
                      className={`bdportal ${styles.toggleButton}`}
                      style={{
                        background: '#f0f0f0',
                        color: '#522D6E',
                        padding: '1rem',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ddd',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isAdvancedFiltersVisible ? 'Show Less Filters' : 'Show More Filters'}
                    </Button>
                  </>
                )}
              </div>

              {/* Group 2 - Advanced Filters (Toggleable) */}
              {isAdvancedFiltersVisible && !isMoleculeSearchInputFocussed && (
                <div
                  className={`bdportal flex items-center space-x-4 ${styles.filterGroup} ${styles.advancedFilters}`}
                  style={{ minHeight: '3.5rem' }}
                >
                  {isPortfolioFilterVisible && (
                    <Dropdown
                      options={getOpportunityTypeOptions()}
                      optionLabel="label"
                      optionValue="ID"
                      value={
                        filterIsPortfolioOpportunity == null
                          ? null
                          : filterIsPortfolioOpportunity
                            ? 'Yes'
                            : 'No'
                      }
                      placeholder="Opportunity Type"
                      onChange={e => handleIsPortfoliOpportunityOptionSelect(e.value)}
                      className="bdportal p-1 border"
                      style={{ maxWidth: '15rem', marginLeft: '0' }}
                      showClear
                    />
                  )}

                  <div className={styles.dateRangesSection} style={{ marginLeft: '0' }}>
                    <Calendar
                      value={filterStartAndEnddates}
                      onChange={e => handleFilterStartAneEndDatesChange(e.value)}
                      selectionMode="range"
                      readOnlyInput
                      hideOnRangeSelection
                      style={{ height: '3rem', marginLeft: '0' }}
                      inputStyle={{ padding: '1rem' }}
                      placeholder="Created Dates"
                      pt={{
                        input: {
                          root: {
                            style: {
                              background: "var(--bg-table-row)",
                              border: '1px solid var(--border-primary)'
                            }
                          }
                        }
                      }}
                    />
                    {filterStartAndEnddates &&
                      (filterStartAndEnddates[0] || filterStartAndEnddates[1]) && (
                        <img
                          className={styles.dateRangeClearButton}
                          src={ClearIcon}
                          alt="Clear Date Range"
                          style={{
                            cursor: 'pointer',
                            marginLeft: '0',
                          }}
                          onClick={() => handleFilterStartAneEndDatesChange(null)}
                        />
                      )}
                  </div>

                  <Button
                    onClick={handleMenuClick}
                    style={{
                      padding: '0 1rem',
                      height: '3rem',
                      marginLeft: '0',
                      background: 'var(--bg-navbar)',
                      color: "white"
                    }}
                    className="bdportal rounded-lg flex items-center"
                  >
                    $ Revenue
                  </Button>
                  <OverlayPanel ref={op} className={styles.userDropdown}>
                    <div className={styles.annualRevenueRange}>
                      <div className={styles.annualRevenueRangeFilterValuesLabels}>
                        <span>{filterAnnualRevenueRange[0]}</span>
                        <span>{filterAnnualRevenueRange[1]}</span>
                      </div>

                      <Slider
                        value={filterAnnualRevenueRange}
                        onChange={(e: SliderChangeEvent) =>
                          handleFilterEstimatedAnnualRevenue(e.value as [number, number])
                        }
                        style={{
                          width: '40rem',
                          marginLeft: '0',
                        }}
                        range
                        max={1000000}
                        min={0}
                        placeholder="Annual Revenue"
                      />
                      <Button
                        onClick={() => handleFilterEstimatedAnnualRevenue([0, 0])}
                        style={{
                          padding: '0 1rem',
                          height: '1.5rem',
                          marginLeft: '0',
                        }}
                        className="bdportal bg-purple-100 text-purple-700 rounded flex items-center"
                      >
                        Clear
                      </Button>
                    </div>
                  </OverlayPanel>

                  {(viewType === 'List' || viewType === 'PortfolioOpportunities') && (
                    <>
                      <Dropdown
                        options={dropdownDetails?.[boardType].Stage}
                        optionLabel="Stage"
                        optionValue="ID"
                        value={filterStage}
                        placeholder="Stage"
                        onChange={e => setFilterStage(e.value)}
                        className="bdportal p-1 border"
                        style={{ maxWidth: '15rem', marginLeft: '0' }}
                        showClear
                      />
                      <Dropdown
                        options={dropdownDetails?.[boardType].Status}
                        optionLabel="Status"
                        optionValue="ID"
                        value={filterStatus}
                        placeholder="Status"
                        onChange={e => setFilterStatus(e.value)}
                        className="bdportal p-1 border"
                        style={{ maxWidth: '15rem', marginLeft: '0' }}
                        showClear
                      />
                    </>
                  )}

                  <AsyncSelect
                    loadOptions={fetchSuggestionsWithDelayForSuppliers}
                    onChange={option => {
                      handlePrimarySupplierSelect(option?.value || '');
                    }}
                    maxMenuHeight={500}
                    defaultOptions
                    placeholder="Select Supplier"
                    className="z-1000 ml-0"
                    value={
                      filterPrimarySupplier
                        ? {
                            label: filterPrimarySupplier,
                            value: filterPrimarySupplier,
                          }
                        : null
                    }
                    styles={{
                      menu: provided => ({ ...provided, zIndex: 20 }),
                      control: (base, _state) => ({
                        ...base,
                        height: '53px',
                        minHeight: '53px',
                        width: '15rem',
                        marginLeft: '0',
                        background: "var(--bg-table-row)",
                        border: "1px solid var(--border-primary)",
                        borderRadius: '0.5rem'
                      }),
                    }}
                    isClearable
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
      {isNewOpportunityModalvisible && (
        <AddOpportunity
          visible={isNewOpportunityModalvisible}
          boardType={boardType}
          closeModal={handleCloseNewOpportunityModal}
        />
      )}

      {isPortfolioOpportunitiesModalVisible && (
        <PortfolioOpportunitiesModal
          visibility={isPortfolioOpportunitiesModalVisible}
          toggleVisibility={handleClosePortfolioOpportunitiesModal}
        />
      )}
    </div>
  );
};

export default observer(OpportunityFilter);
